import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteContent } from '../data/mock';
import { Phone, MapPin, Heart, MessageCircle, Navigation, User } from 'lucide-react';

const ContactSection = () => {
  const { language } = useLanguage();
  const content = siteContent[language].contact;

  return (
    <section id="contact" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-orange-800 mb-4">
            {content.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-orange-600 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
          <div className="mt-6 flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full" />
          </div>
        </div>

        {/* Contact Cards */}
        <div className="space-y-6">
          {/* Phone Card */}
          <div className="p-6 md:p-8 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100 shadow-lg">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-orange-400 to-amber-400 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Phone className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-lg md:text-xl font-bold text-orange-800 mb-2">
                  {content.phone}
                </h3>
                <a
                  href="tel:+919723537037"
                  className="text-2xl md:text-3xl font-bold text-orange-600 hover:text-orange-700 transition-colors"
                >
                  {content.phoneNumber}
                </a>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:+919723537037"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Phone className="w-5 h-5" />
                {language === 'gu' ? 'કોલ કરો' : 'Call Now'}
              </a>
              <a
                href="https://api.whatsapp.com/send?phone=919723537037"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Location Card */}
          <div className="p-6 md:p-8 bg-white rounded-2xl shadow-lg border border-orange-100">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-amber-400 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <MapPin className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-orange-800 mb-2">
                  {content.location}
                </h3>
                <p className="text-gray-700 text-base md:text-lg mb-4">
                  {siteContent[language].ashram.address}
                </p>
                <a
                  href="https://maps.google.com/?q=MG6R+W23,+Navapara,+Saduthala,+Gujarat+384315,+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <Navigation className="w-5 h-5" />
                  {language === 'gu' ? 'દિશાનિર્દેશ મેળવો' : 'Get Directions'}
                </a>
              </div>
            </div>
          </div>

          {/* Blessings Card */}
          <div className="p-6 md:p-8 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl text-center">
            <Heart className="w-14 h-14 md:w-16 md:h-16 text-orange-500 mx-auto mb-4" />
            <p className="text-orange-800 font-semibold text-lg md:text-xl">
              {language === 'gu' 
                ? 'બાપુના આશીર્વાદ સદા તમારી સાથે છે' 
                : "Bapu's blessings are always with you"}
            </p>
            <p className="text-orange-600 mt-2 text-2xl md:text-3xl font-bold">
              "યે દિન ભી ચલેજાયેગે"
            </p>
          </div>

          {/* Created By Kevin Patel Card */}
          <div className="p-6 md:p-8 bg-white rounded-2xl shadow-lg border border-orange-200 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <div>
                <p className="text-gray-500 text-sm md:text-base mb-1">
                  {language === 'gu' ? 'દ્વારા બનાવેલ' : 'Created By'}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-orange-800">
                  Kevin Patel
                </h3>
                <p className="text-orange-600 mt-2 text-sm md:text-base">
                  {language === 'gu' 
                    ? 'બાપુના ભક્ત' 
                    : 'A Devotee of Bapu'}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Heart className="w-5 h-5 text-red-500" />
                <span className="text-gray-600 text-sm">
                  {language === 'gu' 
                    ? 'ભક્તિ સાથે બનાવેલ' 
                    : 'Made with Devotion'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
